import type {
  LaborDefinition,
  ModuleDefinition,
  Vec2,
} from "./phase-three-types";

export const ROUND_CLOCK_LABOR_SYNERGY_ID = "round_clock_labor";

export interface CircleTraversalCandidate {
  start: Vec2;
  last: Vec2;
  firstDirection?: number;
  previousDirection?: number;
  pathLength: number;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  signedTurnRadians: number;
  lastQualifiedMoveAtMs: number;
  departedStart: boolean;
  points: Vec2[];
}

export interface CircleTraversalTracker {
  nextSampleAtMs: number;
  candidate?: CircleTraversalCandidate;
}

export interface CircleTraversalResult {
  completed: boolean;
  candidate?: CircleTraversalCandidate;
}

export function cancelCircleTraversal(
  tracker: CircleTraversalTracker,
  nowMs: number,
  sampleIntervalMs: number,
): void {
  tracker.candidate = undefined;
  tracker.nextSampleAtMs = nowMs + sampleIntervalMs;
}

export function updateCircleTraversal(
  tracker: CircleTraversalTracker,
  rule: Extract<LaborDefinition, { kind: "circle_traversal" }>,
  position: Vec2,
  nowMs: number,
  normallyMoved: boolean,
): CircleTraversalResult {
  const existing = tracker.candidate;
  if (
    existing &&
    (!normallyMoved && nowMs - existing.lastQualifiedMoveAtMs > rule.idleResetMs)
  ) {
    cancelCircleTraversal(tracker, nowMs, rule.sampleIntervalMs);
  }
  if (nowMs + 1e-9 < tracker.nextSampleAtMs) {
    return { completed: false, candidate: tracker.candidate };
  }
  tracker.nextSampleAtMs = nowMs + rule.sampleIntervalMs;
  if (!normallyMoved) {
    return { completed: false, candidate: tracker.candidate };
  }
  if (!tracker.candidate) {
    tracker.candidate = createCandidate(position, nowMs);
    return { completed: false, candidate: tracker.candidate };
  }

  const candidate = tracker.candidate;
  const dx = position.x - candidate.last.x;
  const dy = position.y - candidate.last.y;
  const segmentLength = Math.hypot(dx, dy);
  if (segmentLength + 1e-9 < rule.minSegmentDistance) {
    return { completed: false, candidate };
  }

  const direction = Math.atan2(dy, dx);
  candidate.firstDirection ??= direction;
  if (candidate.previousDirection !== undefined) {
    candidate.signedTurnRadians += signedAngleDelta(
      candidate.previousDirection,
      direction,
    );
  }
  candidate.previousDirection = direction;
  candidate.pathLength += segmentLength;
  candidate.last = { ...position };
  candidate.minX = Math.min(candidate.minX, position.x);
  candidate.maxX = Math.max(candidate.maxX, position.x);
  candidate.minY = Math.min(candidate.minY, position.y);
  candidate.maxY = Math.max(candidate.maxY, position.y);
  candidate.lastQualifiedMoveAtMs = nowMs;
  candidate.points.push({ ...position });
  if (candidate.pathLength > rule.minPathLength * 4) {
    // Keep the 100 ms trajectory state bounded on long journeys that never
    // close. A valid loop must close well before four minimum circumferences.
    tracker.candidate = createCandidate(position, nowMs);
    return { completed: false, candidate: tracker.candidate };
  }

  const closeDistance = Math.hypot(
    position.x - candidate.start.x,
    position.y - candidate.start.y,
  );
  if (closeDistance > rule.closeDistance) {
    candidate.departedStart = true;
  }
  if (
    candidate.departedStart &&
    closeDistance <= rule.minSegmentDistance * 2 + 1e-9 &&
    candidate.pathLength + 1e-9 < rule.minPathLength
  ) {
    // Returning exactly to the start before enough path has accumulated is a
    // partial lobe (the characteristic exploit in a figure-eight), not half of
    // a valid circle. Start a fresh candidate at that crossing point.
    tracker.candidate = createCandidate(position, nowMs);
    return { completed: false, candidate: tracker.candidate };
  }
  const closingTurn =
    candidate.firstDirection !== undefined &&
    candidate.previousDirection !== undefined
      ? signedAngleDelta(candidate.previousDirection, candidate.firstDirection)
      : 0;
  const absoluteClosedTurn = Math.abs(
    candidate.signedTurnRadians + closingTurn,
  );
  const windingCenter = {
    x: (candidate.minX + candidate.maxX) / 2,
    y: (candidate.minY + candidate.maxY) / 2,
  };
  const absoluteWinding = Math.abs(
    candidate.points.slice(1).reduce(
      (sum, point, index) =>
        sum + signedAngleDelta(
          Math.atan2(
            candidate.points[index].y - windingCenter.y,
            candidate.points[index].x - windingCenter.x,
          ),
          Math.atan2(
            point.y - windingCenter.y,
            point.x - windingCenter.x,
          ),
        ),
      0,
    ),
  );
  const completed =
    candidate.pathLength + 1e-9 >= rule.minPathLength &&
    candidate.maxX - candidate.minX + 1e-9 >= rule.minSpanX &&
    candidate.maxY - candidate.minY + 1e-9 >= rule.minSpanY &&
    closeDistance <= rule.closeDistance + 1e-9 &&
    absoluteClosedTurn + 1e-6 >= rule.minAbsoluteTurnRadians &&
    absoluteWinding + 1e-6 >= rule.minAbsoluteTurnRadians;
  if (!completed) {
    return { completed: false, candidate };
  }
  tracker.candidate = createCandidate(position, nowMs);
  return { completed: true, candidate: tracker.candidate };
}

export function validateLaborDefinition(
  module: ModuleDefinition,
  hiddenEffectIds?: ReadonlySet<string>,
): string[] {
  const labor = module.labor;
  if (!labor) {
    // Structured P4.19 labor is opt-in so pre-existing labor modules remain
    // compatible until they are migrated through the workshop.
    return false
      ? ["打工技能必须配置结构化labor效果"]
      : [];
  }
  const errors: string[] = [];
  if (module.category !== "labor") {
    errors.push("只有打工技能可以配置labor效果");
  }
  if (
    hiddenEffectIds &&
    "hiddenEffectId" in labor &&
    !hiddenEffectIds.has(labor.hiddenEffectId)
  ) {
    errors.push(`引用了未知隐藏效果 ${labor.hiddenEffectId}`);
  }
  const positive = (value: number): boolean =>
    Number.isFinite(value) && value > 0;
  if (labor.kind === "movement_distance") {
    if (
      !positive(labor.payoutDistance) ||
      !positive(labor.goldPerPayout) ||
      !positive(labor.achievementDistance)
    ) {
      errors.push("移动距离收益参数必须为正数");
    }
  } else if (labor.kind === "circle_traversal") {
    if (
      !positive(labor.sampleIntervalMs) ||
      !positive(labor.minSegmentDistance) ||
      !positive(labor.minPathLength) ||
      !positive(labor.minSpanX) ||
      !positive(labor.minSpanY) ||
      !positive(labor.closeDistance) ||
      !positive(labor.minAbsoluteTurnRadians) ||
      !positive(labor.idleResetMs) ||
      !positive(labor.goldPerCompletion) ||
      !Number.isInteger(labor.achievementCount) ||
      labor.achievementCount <= 0
    ) {
      errors.push("圆周检测参数无效");
    }
  } else if (labor.kind === "stationary_work") {
    if (
      !positive(labor.payoutIntervalMs) ||
      !positive(labor.goldPerPayout) ||
      !positive(labor.movingDrainIntervalMs) ||
      !positive(labor.movingDrainGold) ||
      !positive(labor.achievementStationaryMs)
    ) {
      errors.push("静止收益或移动扣款参数无效");
    }
  } else if (labor.kind === "route_leg") {
    if (
      labor.route !== "core_outer_gate" ||
      !positive(labor.endpointRadius) ||
      !positive(labor.goldPerLeg) ||
      !Number.isInteger(labor.achievementLegs) ||
      labor.achievementLegs <= 0
    ) {
      errors.push("运输单程参数无效");
    }
  } else if (labor.kind === "kill_bonus") {
    if (
      !positive(labor.goldPerOwnedKill) ||
      !positive(labor.achievementBonusGold)
    ) {
      errors.push("击杀额外金币参数无效");
    }
  } else if (labor.kind === "gate_to_gate_patrol") {
    if (!positive(labor.endpointRadius) || !positive(labor.goldPerPatrol)) {
      errors.push("城门巡逻参数无效");
    }
  } else if (labor.kind === "team_repair_payout") {
    if (
      !positive(labor.allyRadius) ||
      !positive(labor.repairPerPayout) ||
      !positive(labor.goldPerPayout) ||
      labor.ownRepairMultiplier < 1
    ) {
      errors.push("团队修复结算参数无效");
    }
  } else if (
    !positive(labor.repairPerPayout) ||
    !positive(labor.goldPerPayout)
  ) {
    errors.push("修复结算参数无效");
  }
  return errors;
}

function createCandidate(position: Vec2, nowMs: number): CircleTraversalCandidate {
  return {
    start: { ...position },
    last: { ...position },
    pathLength: 0,
    minX: position.x,
    maxX: position.x,
    minY: position.y,
    maxY: position.y,
    signedTurnRadians: 0,
    lastQualifiedMoveAtMs: nowMs,
    departedStart: false,
    points: [{ ...position }],
  };
}

function signedAngleDelta(from: number, to: number): number {
  let delta = to - from;
  while (delta > Math.PI) delta -= Math.PI * 2;
  while (delta < -Math.PI) delta += Math.PI * 2;
  return delta;
}
